import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import {
  normalizeTransportRouteDetail,
  type RouteOrderDto,
  type TransportRouteDetailDto,
} from '../dto/transport';
import { canDeleteTransportRoute, canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { TransportAllocatedCost } from '../modules/transports/TransportAllocatedCost';
import { mapTransportMutationErrorToMessage, renderTransportTypeTag } from '../modules/transports/transportRouteViews';
import { buildTransportRouteLabel } from '../modules/orders/transportAddressUtils';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCargoQuantitySummary } from '../utils/cargoQuantity';
import { formatMoneyPl } from '../utils/currency';
import { formatUnit } from '../utils/units';
import {
  deriveOrderCoverage,
  findStaleCargoQuantityWarnings,
  formatStaleCargoQuantityWarningItems,
  getEffectiveTransportCargoItems,
  hasMixedCargoQuantityUnits,
  type AllocationMethod,
  type SharedTransportCargoItem,
} from '../components/sharedTransportFormUtils';
import { getTransportOrderProductDisplayName } from '../components/sharedTransportFormRows';
import {
  calculateTransportCostAllocation,
  commercialAllocationMethodLabelKey,
  commercialAllocationWarningKey,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';
import {
  DetailField,
  DetailSection,
  MetricTile,
  RouteJourney,
  ValueGrid,
} from './TransportRouteDetailView';
import './TransportRouteDetail.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

function productToCargoItem(orderId: string, product: OrderOptionDto['products'][number]): SharedTransportCargoItem {
  return {
    orderId,
    orderProductId: product.id,
    productName: getTransportOrderProductDisplayName(product),
    quantity: Number(product.quantity || 0),
    unit: product.unit,
    grossWeight: product.grossWeight,
    width: product.width,
    height: product.height,
    length: product.length,
    thickness: product.thickness,
  };
}

function wouldAddOrdersCreateMixedQuantityAllocation(
  route: TransportRouteDetailDto,
  availableOrders: OrderOptionDto[],
  orderIds: string[] = [],
  allocationMethod?: string,
): boolean {
  if (allocationMethod !== 'QUANTITY' || orderIds.length === 0) {
    return false;
  }

  const existingCargoItems = getEffectiveTransportCargoItems(route).map((item) => ({
    orderId: item.orderId,
    orderProductId: item.orderProductId,
    productName: item.productName,
    quantity: item.quantity,
    unit: item.unit,
    grossWeight: item.grossWeight,
    width: item.width,
    height: item.height,
    length: item.length,
    thickness: item.thickness,
  }));
  const selectedOrderIds = new Set(orderIds);
  const newCargoItems = availableOrders
    .filter((order) => selectedOrderIds.has(order.id))
    .flatMap((order) => order.products.map((product) => productToCargoItem(order.id, product)));

  return hasMixedCargoQuantityUnits([...existingCargoItems, ...newCargoItems]);
}

function getDetailCargoItemKey(item: SharedTransportCargoItem): string {
  return item.id || `${item.orderId}:${item.orderProductId || item.productName}`;
}

const TransportRouteDetail: React.FC = () => {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [route, setRoute] = useState<TransportRouteDetailDto | null>(null);
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [addOrderModalVisible, setAddOrderModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addOrderForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const watchedAddOrderIds = Form.useWatch('orderIds', addOrderForm) as string[] | undefined;
  const watchedAddAllocationMethod = Form.useWatch('allocationMethod', addOrderForm) as AllocationMethod | undefined;
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canDeleteTransport = canDeleteTransportRoute(currentUser);
  const canViewTransportCosts = authService.canViewTransportCosts();

  useEffect(() => {
    if (!route) {
      return;
    }

    statusForm.setFieldsValue({
      status: route.status,
      notes: route.notes || undefined,
    });
  }, [route, statusForm]);

  const fetchRoute = useCallback(async (transportId: string) => {
    setLoading(true);

    try {
      const response = await api.get(`/transport-routes/${transportId}`);
      setRoute(normalizeTransportRouteDetail(response.data, 'transport.detail'));
    } catch (error) {
      message.error(t('transport:errorFetchingRoute'));
      navigate('/app/transports');
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/orders');
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.detail.availableOrders'));
    } catch (error) {
      message.error(t('transport:errorFetchingOrders'));
    }
  }, [t]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const requests = [fetchRoute(id)];
    if (canManageTransports) {
      requests.push(fetchOrders());
    }

    void Promise.all(requests);
  }, [canManageTransports, fetchOrders, fetchRoute, id]);

  const handleAddOrders = async (values: { orderIds: string[]; allocationMethod?: string }) => {
    if (!id) {
      return;
    }

    if (route && wouldAddOrdersCreateMixedQuantityAllocation(route, availableOrders, values.orderIds, values.allocationMethod)) {
      message.error(t('transport:allocation.mixedQuantityUnitsBlocked'));
      return;
    }

    setSubmitting(true);

    try {
      await api.post(`/transport-routes/${id}/orders`, {
        orders: values.orderIds.map((orderId) => ({ orderId })),
        allocationMethod: values.allocationMethod,
      });

      message.success(t('transport:ordersAddedSuccessfully'));
      setAddOrderModalVisible(false);
      addOrderForm.resetFields();
      await fetchRoute(id);
    } catch (error: any) {
      if (error?.response?.data?.code === 'TRANSPORT_HAS_NO_ORDERS') {
        message.error(t('transport:errorTransportHasNoOrders'));
      } else {
        message.error(error?.response?.data?.error || t('transport:errorAddingOrders'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveOrder = async (orderId: string) => {
    if (!id) {
      return;
    }

    try {
      const response = await api.delete(`/transport-routes/${id}/orders/${orderId}`);

      message.success(t('transport:orderRemovedSuccessfully'));

      if (response.data?.deleted) {
        navigate('/app/transports');
        return;
      }

      await fetchRoute(id);
    } catch (error: any) {
      if (error?.response?.data?.code === 'TRANSPORT_HAS_NO_ORDERS') {
        message.error(t('transport:errorTransportHasNoOrders'));
      } else {
        message.error(error?.response?.data?.error || t('transport:errorRemovingOrder'));
      }
    }
  };

  // Owner request: the detail page had no whole-transport delete — only the
  // list page's row action. Same DELETE /transport-routes/:id endpoint and
  // canDeleteTransportRoute policy as the list, so the two surfaces can never
  // grant different access. WZ_TRANSPORT_LINK_EXISTS is mapped to a Polish
  // message via the shared transportRouteViews helper instead of the raw
  // English backend string.
  const handleDeleteTransport = async () => {
    if (!id) {
      return;
    }

    setDeleting(true);

    try {
      await api.delete(`/transport-routes/${id}`);
      message.success(t('transport:messages.deleteSuccess'));
      navigate('/app/transports');
    } catch (error: any) {
      const mapped = mapTransportMutationErrorToMessage(t, error?.response?.data);
      message.error(mapped || error?.response?.data?.error || t('transport:messages.deleteError'));
      setDeleteModalVisible(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdate = async (values: { status: string; notes?: string }) => {
    if (!id) {
      return;
    }

    setSubmitting(true);

    try {
      await api.put(`/transport-routes/${id}`, values);
      message.success(t('transport:statusUpdatedSuccessfully'));
      setStatusModalVisible(false);
      statusForm.resetFields();
      await fetchRoute(id);
    } catch (error: any) {
      if (error?.response?.data?.code === 'TRANSPORT_HAS_NO_ORDERS') {
        message.error(t('transport:errorTransportHasNoOrders'));
      } else {
        message.error(error?.response?.data?.error || t('transport:errorUpdatingStatus'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statusTag = (status: string) => (
    <Tag>
      {t(`transport:status.${status.toLowerCase()}`, { defaultValue: status })}
    </Tag>
  );

  // S36/S37 (coordinator triage, 2026-07-07): the LDM/weight/volume/percent
  // columns below rendered raw `.toFixed()` output — "60.000" (dot decimal,
  // no thousands grouping) instead of Polish "60,000". Same pl-PL
  // formatting convention as formatCurrencyAmount below and
  // formatConversionValue (utils/dimensionConversion.ts) for m²/m³ — single
  // local helper so every metric cell on this page agrees, rather than each
  // column hand-rolling its own toFixed/toLocaleString call.
  const formatMetricNumber = (value: number | null | undefined, fractionDigits: number): string =>
    Number(value || 0).toLocaleString('pl-PL', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  if (loading || !route) {
    return (
      <div className="transport-route-detail__loading">
        <Spin size="large" />
      </div>
    );
  }

  const availableToAdd = availableOrders.filter(
    (order) => !route.routeOrders.some((routeOrder) => routeOrder.orderId === order.id)
  );
  const totalAllocatedCost = route.routeOrders.reduce(
    (sum, routeOrder) => sum + Number(routeOrder.allocatedTransportCost || 0),
    0
  );
  const totalWeight = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.weight || 0), 0);
  const totalVolume = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.volume || 0), 0);
  // MED (m² exposure): the detail summary emphasized LDM/m³ but omitted m² —
  // match the form's "Przeliczniki ładunku" trio (formatMetricsSummary in
  // SharedTransportForm.tsx), which already shows m² / m³ / LDM together
  // whenever area is present. No legacy top-level route.totalAreaSqm column
  // exists (unlike totalWeight/totalVolume), so this sums the per-order
  // areaSqm the same way totalWeight/totalVolume above already do.
  const totalAreaSqm = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.areaSqm || 0), 0);
  const totalCargoQuantitySummary = formatCargoQuantitySummary(
    getEffectiveTransportCargoItems(route),
    i18n.language,
  );
  const allocationMethod = route.routeOrders[0]?.allocationMethod || 'LDM';
  // "Dodaj zamówienia" allocation picker: same rule as TransportCostsSection —
  // WEIGHT is offered only when it is already this transport's stored value, so
  // new allocations can't pick the retired "Waga" method but a legacy WEIGHT
  // transport still renders/edits without being silently rewritten.
  const addOrdersAllocationMethodOptions = (
    ['LDM', 'CBM', 'SQM', 'QUANTITY', ...(allocationMethod === 'WEIGHT' ? ['WEIGHT'] : [])] as const
  ).map((value) => ({
    value,
    label: t(`transport:allocation.methods.${value.toLowerCase()}`),
  }));
  const displayWeight = route.routeOrders.length > 0 ? totalWeight : route.totalWeight;
  const displayVolume = route.routeOrders.length > 0 ? totalVolume : route.totalVolume;
  const addOrdersBlockedByMixedQuantity = wouldAddOrdersCreateMixedQuantityAllocation(
    route,
    availableOrders,
    watchedAddOrderIds || [],
    watchedAddAllocationMethod || allocationMethod,
  );
  const effectiveCargoItems = getEffectiveTransportCargoItems(route);
  // HOTEL (HIGH, "warn, never block"): a persisted cargo line's loaded
  // quantity can exceed the order product's CURRENT quantity when the order
  // was edited down after this transport was built. Non-blocking detection
  // only — no auto-resync, no save blocking. See findStaleCargoQuantityWarnings
  // for why no backend/DTO change was needed.
  const staleQuantityWarnings = findStaleCargoQuantityWarnings(route);
  // Legacy transports without stored cargo items get no (or an empty)
  // costAllocation from the API — recompute from the synthesized rows instead.
  const apiCostAllocation = route.costAllocation && route.costAllocation.items.length > 0
    ? route.costAllocation
    : null;
  const costAllocation = apiCostAllocation || calculateTransportCostAllocation({
    transportCost: route.transportRate ?? route.transportCost ?? 0,
    method: route.costAllocationMethod,
    items: effectiveCargoItems.map((item) => ({
      id: getDetailCargoItemKey(item),
      quantity: item.quantity,
      unit: item.unit,
      goodsValueTotal: item.goodsValueTotal,
      manualAllocatedTransportCost: item.manualAllocatedTransportCost,
      // Product identity + dimensions (mm) feed the BY_SQM / BY_CBM / BY_LDM
      // metric methods (normalizeItem). Without them the client-side fallback
      // allocation raises MISSING_DIMENSIONS for legacy transports even though
      // the cargo items carry dimensions. Display-only fallback path — the saved
      // allocation is untouched. Mirrors the form's cargo-row mapping (commit
      // 88e54105) and productToCargoItem above.
      name: item.productName,
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness,
    })),
  });
  const costAllocationItemsById = new Map(
    costAllocation.items.map((item) => [item.id, item]),
  );
  // MIXED_UNITS is a quantity-split-only advisory ("Podział według ilości może
  // być niepoprawny…"). A persisted allocation may still carry it even after the
  // operator moves the item-level split off BY_COMMERCIAL_QTY (to a metric/value
  // key that does not care about mixed commercial units), so only surface it
  // when the current item method is BY_COMMERCIAL_QTY.
  const visibleCostAllocationWarnings = costAllocation.warnings.filter(
    (warning) =>
      warning.code !== 'MIXED_UNITS' || route.costAllocationMethod === 'BY_COMMERCIAL_QTY',
  );
  const routeLabel = buildTransportRouteLabel(
    route.loadingAddresses,
    route.deliveryAddresses,
    route.originCity,
    route.destinationCity,
  );
  const deliveryDate = route.actualDelivery || route.estimatedDelivery;
  const licensePlates = [route.licensePlate1, route.licensePlate2].filter(Boolean).join(', ');
  // MED (money format): Polish-first amounts everywhere on this page —
  // "4 500,00 PLN" (pl-PL grouping + comma decimal + ISO currency-code
  // suffix), not "4500.00PLN" or a "zł" symbol. S36 (coordinator triage,
  // 2026-07-07, external run #3): this page used to mix formatCurrency's
  // "zł" symbol here with the headline Statistics' "PLN" code — formatMoneyPl
  // in utils/currency.ts is the single home for the ISO-code convention now
  // shared by every money render on the transport list + detail pages.
  const formatCurrencyAmount = (value: number | null | undefined): string =>
    value == null ? '—' : formatMoneyPl(value, route.currency);
  const renderCostAllocationWarning = (
    warning: TransportCostAllocationWarning,
    index: number,
  ) => {
    // Offending product names for MISSING_DIMENSIONS so the operator sees which
    // cargo lines lack dimensions. Unnamed lines are dropped; if none remain we
    // fall back to the generic message with no name suffix.
    const missingDimensionNames =
      warning.code === 'MISSING_DIMENSIONS'
        ? (warning.items || [])
            .map((item) => (item.name || '').trim())
            .filter((name) => name.length > 0)
            .join(', ')
        : '';

    let description: React.ReactNode;
    if (warning.code === 'MANUAL_SUM_MISMATCH') {
      description = t('transport:commercialAllocationWarningDifference', {
        difference: formatCurrencyAmount(costAllocation.summary.allocationDifference),
      });
    } else if (warning.code === 'MISSING_DIMENSIONS' && missingDimensionNames) {
      description = t('transport:missingDimensionsItems', { names: missingDimensionNames });
    }

    return (
      <Alert
        key={`${warning.code}-${warning.itemId || index}`}
        type="warning"
        showIcon
        message={t(`transport:${commercialAllocationWarningKey(warning.code)}`)}
        description={description}
        className="transport-detail-allocation-warning"
      />
    );
  };
  return (
    <div className="transport-route-detail">
      <header className="transport-detail-header">
        <div className="transport-detail-header__main">
          <div className="transport-detail-header__eyebrow">
            {renderTransportTypeTag(t, route.routeOrders.length)}
            {statusTag(route.status)}
          </div>
          <Title level={2} className="transport-detail-header__title" title={routeLabel.tooltip}>
            {routeLabel.label}
          </Title>
          <div className="transport-detail-header__meta">
            <span>{formatDate(route.loadingDate)} → {formatDate(deliveryDate)}</span>
            <Text strong>{route.transportCompany || '—'}</Text>
          </div>
        </div>
        <div className="transport-detail-header__actions">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/transports')}>
            {t('common:back')}
          </Button>
          {canManageTransports ? (
            <>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/app/transports/${id}/edit`)}>
                {t('common:edit')}
              </Button>
              <Button type="primary" onClick={() => setStatusModalVisible(true)}>
                {t('transport:updateStatus')}
              </Button>
            </>
          ) : null}
          {canDeleteTransport ? (
            <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalVisible(true)}>
              {t('transport:messages.deleteButton')}
            </Button>
          ) : null}
        </div>
      </header>

      {/* HOTEL (HIGH, "warn, never block"): the order changed since this
          transport's cargo was built (e.g. quantity reduced) — non-blocking,
          no auto-resync. */}
      {staleQuantityWarnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t('transport:cargo.staleQuantityWarningTitle')}
          description={(
            <ul>
              {formatStaleCargoQuantityWarningItems(staleQuantityWarnings, t).map((line, index) => (
                <li key={staleQuantityWarnings[index].orderProductId}>{line}</li>
              ))}
            </ul>
          )}
          className="transport-detail-alert"
        />
      ) : null}

      <main className="transport-detail-layout">
        <div className="transport-detail-top-grid">
          <div className="transport-detail-primary-stack">
            <DetailSection title={t('transport:detailSections.routeSchedule')}>
              <RouteJourney
                loadingLabel={t('transport:fields.loadingAddresses')}
                deliveryLabel={t('transport:fields.deliveryAddresses')}
                loadingAddresses={route.loadingAddresses}
                deliveryAddresses={route.deliveryAddresses}
                dates={[
                  { label: t('transport:fields.loadingDate'), value: formatDate(route.loadingDate) },
                  { label: t('transport:fields.estimatedDelivery'), value: formatDate(route.estimatedDelivery) },
                  {
                    label: t('transport:fields.actualDelivery'),
                    value: route.actualDelivery ? formatDate(route.actualDelivery) : '—',
                  },
                ]}
                extra={!canViewTransportCosts ? (
                  <>
                    <DetailField label={t('transport:allocation.method')}>
                      {t(`transport:allocation.methods.${allocationMethod.toLowerCase()}`, {
                        defaultValue: allocationMethod,
                      })}
                    </DetailField>
                    {route.notes ? (
                      <DetailField label={t('transport:fields.notes')}>{route.notes}</DetailField>
                    ) : null}
                  </>
                ) : undefined}
              />
            </DetailSection>

            <DetailSection title={t('transport:detailSections.carrierVehicle')}>
              <ValueGrid columns={5}>
                <DetailField label={t('transport:fields.transportCompany')}>
                  {route.transportCompany || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.transportContact')}>
                  {route.transportContact || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.licensePlates')}>
                  {licensePlates || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.driverName')}>
                  {route.driverName || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.driverPhone')}>
                  {route.driverPhone || '—'}
                </DetailField>
              </ValueGrid>
            </DetailSection>
          </div>

          <DetailSection title={t('transport:statistics')}>
            <div className="transport-detail-metrics">
              <MetricTile label={t('transport:totalOrders')} value={route.routeOrders.length} />
              {canViewTransportCosts ? (
                <MetricTile
                  label={t('transport:cost')}
                  value={formatMoneyPl(totalAllocatedCost, 'PLN')}
                  emphasis
                />
              ) : null}
              {totalAreaSqm > 0 ? (
                <MetricTile
                  label={t('transport:totalArea')}
                  value={`${formatMetricNumber(totalAreaSqm, 3)} m²`}
                />
              ) : null}
              <MetricTile
                label={t('transport:totalWeight')}
                value={`${formatMetricNumber(displayWeight, 2)} kg`}
              />
              <MetricTile
                label={t('transport:totalVolume')}
                value={`${formatMetricNumber(displayVolume, 3)} m³`}
              />
              <MetricTile label={t('transport:totalPackages')} value={totalCargoQuantitySummary} />
            </div>
          </DetailSection>
        </div>

        {canViewTransportCosts ? (
          <DetailSection title={t('transport:detailSections.costs')}>
            <ValueGrid columns={3} className="transport-detail-cost-grid">
              <MetricTile
                label={t('transport:fields.transportRate')}
                value={formatCurrencyAmount(route.transportRate)}
                emphasis
              />
              <DetailField label={t('transport:allocation.method')}>
                {t(`transport:allocation.methods.${allocationMethod.toLowerCase()}`, {
                  defaultValue: allocationMethod,
                })}
              </DetailField>
              <DetailField label={t('transport:commercialAllocationMethod')}>
                {t(`transport:${commercialAllocationMethodLabelKey(route.costAllocationMethod)}`)}
              </DetailField>
              {route.notes ? (
                <DetailField label={t('transport:fields.notes')} className="transport-detail-notes">
                  {route.notes}
                </DetailField>
              ) : null}
            </ValueGrid>
          </DetailSection>
        ) : null}

        <DetailSection
            title={t('transport:linkedOrders')}
            className="transport-detail-section--linked-orders"
            extra={
              canManageTransports ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddOrderModalVisible(true)}
                  disabled={availableToAdd.length === 0}
                >
                  {t('transport:addOrders')}
                </Button>
              ) : null
            }
          >
          <div className="transport-detail-card-list" data-testid="transport-linked-order-list">
            {route.routeOrders.map((record: RouteOrderDto) => {
              const orderCargoItems = effectiveCargoItems.filter((item) => item.orderId === record.orderId);
              const products = orderCargoItems
                .map((item) => `${item.productName} (${item.quantity} ${formatUnit(item.unit)})`)
                .join(', ') || '—';
              const coverage = deriveOrderCoverage(route, record.orderId);
              const coverageText = coverage.length === 0
                ? '—'
                : coverage.map((entry) => t('transport:cargo.coverageValue', {
                    loaded: entry.loadedQuantity,
                    ordered: entry.orderedQuantity,
                    unit: formatUnit(entry.unit),
                  })).join(', ');
              return (
                <article className="transport-detail-order" key={record.orderId}>
                  <div className="transport-detail-order__heading">
                    <div className="transport-detail-order__identity">
                      <Button type="link" onClick={() => navigate(`/app/orders/${record.order.id}`)}>
                        {record.order.displayOrderNumber || record.order.orderNumber}
                      </Button>
                      <Text>{record.order.customerName || '—'}</Text>
                    </div>
                    {canManageTransports ? (
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => void handleRemoveOrder(record.orderId)}
                      >
                        {t('common:delete')}
                      </Button>
                    ) : null}
                  </div>
                  <DetailField label={t('transport:table.products')} className="transport-detail-order__products">
                    {products}
                  </DetailField>
                  <div className="transport-detail-order__metrics">
                    <DetailField label={t('transport:packages')}>
                      {formatCargoQuantitySummary(orderCargoItems, i18n.language)}
                    </DetailField>
                    <DetailField label={t('transport:cargo.coverage')}>{coverageText}</DetailField>
                    <DetailField label="LDM">
                      {record.ldm ? formatMetricNumber(record.ldm, 2) : '—'}
                    </DetailField>
                    <DetailField label={t('transport:weight')}>
                      {`${formatMetricNumber(record.weight, 2)} kg`}
                    </DetailField>
                    <DetailField label={t('transport:volume')}>
                      {`${formatMetricNumber(record.volume, 3)} m³`}
                    </DetailField>
                    <DetailField label={t('transport:allocation.percent')}>
                      {`${formatMetricNumber(record.allocationPercent, 2)}%`}
                    </DetailField>
                    {canViewTransportCosts ? (
                      <DetailField label={t('transport:allocation.cost')}>
                        <TransportAllocatedCost
                          provenanceClassName="transport-detail-order__cost-provenance"
                          allocatedTransportCost={record.allocatedTransportCost}
                          originalCurrency={record.originalCurrency}
                          originalAmount={record.originalAmount}
                          exchangeRateUsed={record.exchangeRateUsed}
                        />
                      </DetailField>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </DetailSection>

        {canViewTransportCosts ? (
          <DetailSection
            title={t('transport:commercialAllocationTitle')}
            className="transport-detail-section--commercial-allocation"
          >
            <div className="transport-detail-card-list" data-testid="transport-cargo-cost-list">
              {effectiveCargoItems.map((item) => {
                const allocationItem = costAllocationItemsById.get(getDetailCargoItemKey(item));
                return (
                  <article className="transport-detail-cargo-cost" key={getDetailCargoItemKey(item)}>
                    <div className="transport-detail-cargo-cost__heading">
                      <div className="transport-detail-cargo-cost__product">{item.productName}</div>
                      <Text>{item.quantity} {formatUnit(item.unit)}</Text>
                    </div>
                    <div className="transport-detail-cargo-cost__grid">
                      <DetailField label={t('transport:commercialAllocationGoodsValue')}>
                        {formatCurrencyAmount(allocationItem?.goodsValueTotal)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationAllocatedTransport')}>
                        {formatCurrencyAmount(allocationItem?.allocatedTransportCost)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationGoodsPerUnit')}>
                        {formatCurrencyAmount(allocationItem?.goodsCostPerUnit)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerUnit')}>
                        {formatCurrencyAmount(allocationItem?.transportCostPerUnit)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerSqm')}>
                        {formatCurrencyAmount(allocationItem?.transportCostPerAreaSqm)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerCbm')}>
                        {formatCurrencyAmount(allocationItem?.transportCostPerVolumeM3)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerLdm')}>
                        {formatCurrencyAmount(allocationItem?.transportCostPerLdm)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTotalPerUnit')}>
                        {formatCurrencyAmount(allocationItem?.landedCostPerUnit)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTotal')}>
                        {formatCurrencyAmount(allocationItem?.landedCostTotal)}
                      </DetailField>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="transport-detail-allocation-summary">
              <div className="transport-detail-allocation-summary__item">
                {t('transport:commercialAllocationSummaryGoodsValue', {
                  value: formatCurrencyAmount(costAllocation.summary.totalGoodsValue),
                })}
              </div>
              <div className="transport-detail-allocation-summary__item">
                {t('transport:commercialAllocationSummaryTransport', {
                  value: formatCurrencyAmount(costAllocation.summary.totalAllocatedTransportCost),
                })}
              </div>
              <div className="transport-detail-allocation-summary__item transport-detail-allocation-summary__item--total">
                <strong>
                  {t('transport:commercialAllocationSummaryTotal', {
                    value: formatCurrencyAmount(costAllocation.summary.totalLandedCost),
                  })}
                </strong>
              </div>
              {costAllocation.summary.allocationDifference !== 0 ? (
                <Text type="warning" className="transport-detail-allocation-summary__difference">
                  {t('transport:commercialAllocationSummaryDifference', {
                    value: formatCurrencyAmount(costAllocation.summary.allocationDifference),
                  })}
                </Text>
              ) : null}
            </div>
            {visibleCostAllocationWarnings.map(renderCostAllocationWarning)}
          </DetailSection>
        ) : null}
      </main>

      <Modal
        title={t('transport:addOrdersToRoute')}
        open={addOrderModalVisible}
        onCancel={() => {
          setAddOrderModalVisible(false);
          addOrderForm.resetFields();
        }}
        footer={null}
      >
        <Form form={addOrderForm} layout="vertical" onFinish={handleAddOrders}>
          <Form.Item
            name="orderIds"
            label={t('transport:selectOrders')}
            rules={[{ required: true, message: t('transport:pleaseSelectOrders') }]}
          >
            <Select
              mode="multiple"
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={availableToAdd.map((order) => ({
                value: order.id,
                label: order.label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="allocationMethod"
            label={t('transport:allocation.method')}
            initialValue={allocationMethod}
          >
            {/* "Waga" (WEIGHT) is dropped from the picker for new/changed
                allocations (rulebook decision #6), matching TransportCostsSection.
                It stays offered only when it is already this transport's stored
                value so a legacy WEIGHT transport renders and edits without being
                silently rewritten. */}
            <Select
              options={addOrdersAllocationMethodOptions}
            />
          </Form.Item>
          {addOrdersBlockedByMixedQuantity ? (
            <Alert
              type="warning"
              showIcon
              message={t('transport:allocation.mixedQuantityUnitsBlocked')}
              className="transport-detail-modal-warning"
            />
          ) : null}
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={addOrdersBlockedByMixedQuantity}
            >
              {t('transport:addOrders')}
            </Button>
            <Button
              onClick={() => {
                setAddOrderModalVisible(false);
                addOrderForm.resetFields();
              }}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={t('transport:updateRouteStatus')}
        open={statusModalVisible}
        onCancel={() => {
          statusForm.setFieldsValue({
            status: route.status,
            notes: route.notes || undefined,
          });
          setStatusModalVisible(false);
        }}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleStatusUpdate}>
          <Form.Item
            name="status"
            label={t('transport:fields.status')}
            rules={[{ required: true, message: t('transport:pleaseSelectStatus') }]}
          >
            <Select
              options={[
                { value: 'PLANNED', label: t('transport:status.planned') },
                { value: 'PENDING', label: t('transport:status.pending') },
                { value: 'SCHEDULED', label: t('transport:status.scheduled') },
                { value: 'LOADING', label: t('transport:status.loading') },
                { value: 'IN_TRANSIT', label: t('transport:status.in_transit') },
                { value: 'DELAYED', label: t('transport:status.delayed') },
                { value: 'DELIVERED', label: t('transport:status.delivered') },
                { value: 'CANCELLED', label: t('transport:status.cancelled') },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('transport:notes')}>
            <TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('transport:updateStatus')}
            </Button>
            <Button
              onClick={() => {
                setStatusModalVisible(false);
                statusForm.resetFields();
              }}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={t('transport:messages.deleteButton')}
        open={deleteModalVisible}
        onOk={() => void handleDeleteTransport()}
        onCancel={() => setDeleteModalVisible(false)}
        okButtonProps={{ danger: true, loading: deleting }}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
      >
        <p>{t('transport:messages.deleteConfirmTitle', { number: route.routeNumber })}</p>
      </Modal>
    </div>
  );
};

export default TransportRouteDetail;
